import type { AgentDraft, AvatarCrop } from "@nemo/agent-schema";
import type { CredentialSource } from "./credentials.js";

export type Role = "owner" | "manager" | "member";
/** Who may create a new account right now (server setting). */
export type SignupMode = "open" | "invite" | "closed";

export type ActivityAction = "created" | "edited" | "published" | "unpublished" | "reverted" | "deleted" | "source_added" | "source_removed" | "transferred" | "shared" | "unshared";
export interface FieldChange {
  path: string;
  change: "set" | "changed" | "cleared";
  old?: string | number | boolean | (string | number | boolean)[];
  new?: string | number | boolean | (string | number | boolean)[];
  size?: { from?: number; to?: number; unit: "chars" | "items" };
}
export interface ActivityItem {
  id: number;
  at: string;
  startedAt: string;
  agentId: string;
  agentName: string | null;
  action: ActivityAction;
  version: number | null;
  actor: { kind: "user" | "api_key" | "staff" | "partner" | "system"; id: string | null; label: string };
  via: string;
  client: string | null;
  summary: { fields?: FieldChange[]; first?: boolean; type?: string; extension?: string; host?: string; note?: string };
}
export interface VersionItem {
  version: number;
  publishedAt: string;
  by: string | null;
  current: boolean;
  name: string | null;
  model: string | null;
}
export interface ActivityFilters {
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
  offset?: number;
  limit?: number;
}
export interface Me {
  user: { id: string; email: string; name: string; language: "en" | "es"; emailVerified: boolean };
  csrfToken: string;
  accounts: { id: string; name: string; role: Role; status: "active" | "suspended" }[];
  activeAccountId: string | null;
  staffRole: "super_admin" | "admin" | "support" | null;
  /** `blocked`: show the code (or set-up) screen; nothing else works until it is done. */
  mfa: { enrolled: boolean; done: boolean; required: boolean; blocked: boolean; recoveryCodesLeft: number; nudge: "first" | "trial_end" | null };
}
export interface SessionInfo {
  id: string;
  current: boolean;
  method: string;
  device: string | null;
  createdAt: string;
  lastSeenAt: string;
}
export interface TeamInfo {
  members: { userId: string; email: string; name: string; role: Role; emailVerified: boolean; joinedAt: string; you: boolean }[];
  invitations: { id: string; email: string; role: Role; createdAt: string; expiresAt: string }[];
  yourRole: Role;
}
/** What a person can do on one agent: guests get one of the first three; `owner` is an owner of the owning account. */
export type AgentRole = "viewer" | "editor" | "manager" | "owner";
export type ShareRole = "viewer" | "editor" | "manager";
export interface SharedAgentSummary extends AgentSummary {
  shared: true;
  role: ShareRole;
  ownerAccountName: string;
}
export interface SharingInfo {
  yourRole: AgentRole;
  via: "account" | "share";
  ownerAccountName: string;
  accountTeamSize: number | null;
  people: { userId: string; name: string; email: string; role: ShareRole; you: boolean; createdAt: string }[];
  invitations: { id: string; email: string; role: ShareRole; createdAt: string; expiresAt: string }[];
  transfer: { id: string; toEmail: string; expiresAt: string } | null;
}
export interface TransferOffer {
  id: string;
  agentName: string;
  fromAccountName: string;
  fromName: string;
  toEmail: string;
  expiresAt: string;
  hasLogin: boolean;
  /** Only when signed in as the invited address: the accounts that can receive it. */
  accounts?: { id: string; name: string }[];
}
export interface TransferBrief {
  id: string;
  agentId: string;
  agentName: string;
  toEmail: string;
  expiresAt: string;
  createdAt: string;
}
export type BillingInterval = "month" | "year";
export interface BillingPlan {
  key: string;
  name: string;
  summary: string;
  sort: number;
  prices: { month?: { amount: number; currency: string }; year?: { amount: number; currency: string } };
  trialDays: number;
  limits: { agents: number | null; conversationsPerMonth: number | null; documentsPerAgent: number | null; onlinePagesPerAgent: number | null; teammates: number | null; models: string[] | null; channels: string[]; features: string[] };
}
export interface BillingInfo {
  enforced: boolean;
  plans: BillingPlan[];
  current: {
    plan: { key: string; name: string } | null;
    state: "active" | "trialing" | "grace" | "suspended" | "ended" | "none";
    online: boolean;
    interval: BillingInterval | null;
    status: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    trialEnd: string | null;
    graceEndsAt: string | null;
    cancelsAt: string | null;
    grantedByStaff: boolean;
    limits: BillingPlan["limits"];
  };
  usage: { conversations: { used: number; limit: number | null; periodStart: string }; agents: { used: number; limit: number | null }; teammates: { used: number; limit: number | null } };
  heldAgents: string[];
  invoices: { id: string; status: string; currency: string; amountDue: number; amountPaid: number; periodStart: string | null; periodEnd: string | null; url: string | null; date: string }[];
  canSubscribe: boolean;
  trialEligible: boolean;
}

export interface AccountInfo {
  id: string;
  name: string;
  language: "en" | "es";
  status: string;
  createdAt: string;
  yourRole: Role;
  retentionDays: number;
}

export interface Issue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: Issue[] = [],
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
  get unauthorized() {
    return this.status === 401;
  }
}

export interface AgentSummary {
  id: string;
  name: string;
  status: "draft" | "published" | "unpublished";
  publishedVersion: number | null;
  hasUnpublishedChanges: boolean;
  avatarUrl: string | null;
  updatedAt: string;
}

export interface AgentDetail {
  id: string;
  status: "draft" | "published" | "unpublished";
  publishedVersion: number | null;
  revision: number;
  hasUnpublishedChanges: boolean;
  config: AgentDraft;
  readiness: { ready: boolean; issues: Issue[] };
  /** Present when the agent is shared with this person (not in their own account). */
  shared?: boolean;
  role?: ShareRole;
  /** Present when the agent is over the account's plan: view-only until they upgrade or free room. */
  planBlock?: { message: string; upgradeUrl?: string; reason?: string };
}

export interface OcrEstimate {
  allowed: boolean;
  reasons: string[];
  messages: string[];
  pages: number;
  scannedPages: number;
  usd: number;
  minutes: number;
  limits: { maxPagesPerRun: number; pagesLeftToday: number; usdLeftThisMonth: number };
}
export interface OcrRun {
  id: string;
  sourceId: string;
  pagesPlanned: number;
  pagesDone: number;
  pagesFailed: number;
  status: "running" | "stopping" | "done" | "stopped" | "capped" | "failed";
  note: string;
}

export type SourceStatus = "pending" | "indexing" | "indexed" | "error" | "unsupported";
export interface Source {
  id: string;
  type: string;
  name: string;
  spec: Record<string, unknown>;
  status: SourceStatus;
  error: string | null;
  comingSoon: boolean;
  chunks: number;
  /** Websites only: how often the page is read again. */
  sync?: { cron: string; nextRunAt: string | null; lastRunAt: string | null; paused: boolean; pauseReason: string | null } | null;
}

export interface IntegrationState {
  provider: string;
  available: boolean;
  reason?: string;
  held?: boolean;
  connection: { id: string; provider: string; email: string | null; status: "active" | "needs_reconnect"; lastError: string | null; connectedAt: string; lastUsedAt: string | null } | null;
}
export interface PickerConfig {
  accessToken: string;
  expiresInSec: number;
  clientId: string;
  appId?: string;
  apiKey?: string;
}
export type DriveFileState = "indexed" | "pending" | "not_found" | "no_access" | "unsupported" | "failed";
export interface DriveFiles {
  /** Folder sources only: how many files are in the knowledge. */
  indexedCount?: number;
  run: { status: string; finishedAt: string | null; error: string | null } | null;
  files: { id: string; name: string; status: DriveFileState; note?: string }[];
}

export interface DriveFolder {
  id: string;
  folderId: string;
  folderName: string | null;
  status: "pending" | "active";
  /** Only while pending: the name of the file to create inside the folder. */
  code?: string;
  expiresAt?: string | null;
  verifiedAt: string | null;
  createdAt: string;
  shareWith?: string;
}

export interface ModelOption {
  provider: "anthropic" | "openai" | "google";
  model: string;
  label: string;
  default?: boolean;
}
export interface ModelRegistry {
  generative: ModelOption[];
}

export interface ApiKeyInfo {
  id: string;
  name: string;
  hint: string;
  createdAt: string;
  revokedAt: string | null;
  current: boolean;
}

export interface AppNotification {
  id: string;
  agentId: string;
  agentName: string;
  /** A widget problem, or a model notice such as model_retiring, model_switched, model_fallback. */
  kind: "load_failed" | "chat_failed" | "script_error" | "render_failed" | "lead_delivery_failed" | (string & {});
  severity: "error" | "warning";
  count: number;
  origins: string[];
  firstAt: string;
  lastAt: string;
  message: string;
  advice: string;
  /** For "not in your allowed domains": the sites that can be allowed with one click. */
  blockedSites?: BlockedSite[];
  /** A search-model change that needs the owner (`model_action_required`): shown as a banner until reviewed. */
  actionRequired?: ModelActionDetails;
}

/** What the notification says about the change (display names, dates and the provider's notice). */
export interface ModelActionDetails {
  agentId: string;
  acknowledged: boolean;
  switched: boolean;
  currentModel?: string;
  newModel?: string;
  retiresAt?: string | null;
  switchesOn?: string | null;
  /** The provider's official notice (https), or null when there is none. */
  noticeUrl?: string | null;
}
/** The agent's pending search-model change, in plain terms (no model ids). `{ active: false }` when there is none. */
export type ModelMigration =
  | { active: false }
  | {
      active: true;
      state: "planned" | "building" | "verifying" | "ready" | "switched" | string;
      ready: boolean;
      canSwitch: boolean;
      switched: boolean;
      deadline: string | null;
      daysLeft: number | null;
      progress?: { done: number; total: number };
      acknowledged: boolean;
      tested: boolean;
      undoUntil: string | null;
    };

export interface BlockedSite {
  origin: string;
  count: number;
  lastAt: string;
  /** What Allow adds. */
  entries: string[];
}

export interface SiteCheck {
  id: "script" | "agent" | "allowed" | "display" | "builder" | "tag_manager" | "csp" | "viewport";
  status: "ok" | "warn" | "fail" | "info";
  code: string;
  params?: Record<string, string>;
}

export interface SiteReport {
  ok: boolean;
  finalUrl?: string;
  problem?: "invalid_url" | "blocked" | "timeout" | "unreachable" | "http_error" | "not_html" | "too_large";
  status?: number;
  builder: string | null;
  checks: SiteCheck[];
  entries: string[];
}

export interface AvatarInfo {
  version: number;
  url: string;
  smallUrl: string;
  crop: AvatarCrop;
  width: number | null;
  height: number | null;
  canAdjust: boolean;
  createdAt: string;
}

export interface BlockedSites {
  sites: BlockedSite[];
  suggestion: { domain: string; entries: string[] } | null;
  canAllow: boolean;
}

export type DeliveryKind = "email" | "webhook" | "visitor_recap" | "wix" | "hubspot";
export type DeliveryState = "queued" | "sending" | "sent" | "failed" | "not_connected" | "skipped";
export type LeadStatus = "none" | "pending" | "delivered" | "partial" | "failed";

export interface LeadSummary {
  id: number;
  agentId: string;
  agentName: string;
  createdAt: string;
  channel: string;
  language: string;
  isTest: boolean;
  deliveryStatus: LeadStatus;
  fields: { key: string; label: string; value: string }[];
  /** Where the chat happened and how the visitor arrived. Null for leads from before this was recorded. */
  source: LeadSource | null;
  /** Where the visitor came from at the start of the visit. Null for leads from before tracking. */
  attribution: LeadAttribution | null;
}
export interface LeadAttribution {
  landingUrl?: string;
  referrer?: string;
  /** Campaign tags and advertising click ids that were present, by name. */
  params?: Record<string, string>;
  channel?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  /** Plain name of the source, e.g. "Google Ads". */
  label?: string;
  confidence?: "high" | "medium" | "low";
  firstSeenBy?: "widget" | "derived";
  capturedAt?: string;
}
export interface LeadSource {
  pageUrl?: string;
  firstPageUrl?: string;
  referrer?: string;
  utm?: Record<string, string>;
}
export interface LeadDelivery {
  id: number;
  kind: DeliveryKind;
  target: string;
  status: DeliveryState;
  attempts: number;
  nextAttemptAt: string | null;
  sentAt: string | null;
  error: string | null;
  updatedAt: string;
}
export interface LeadDetail extends LeadSummary {
  conversationId: string | null;
  recapConsent: boolean;
  excerpt: { role: "user" | "assistant"; content: string }[];
  deliveries: LeadDelivery[];
}
export interface LeadFilters {
  agentId?: string;
  status?: LeadStatus | "";
  from?: string;
  to?: string;
  q?: string;
  test?: boolean;
}

const MESSAGES: Record<string, string> = {
  unauthorized: "Enter your API key to continue.",
  invalid_api_key: "That API key is not valid or was revoked.",
  not_found: "We could not find that. It may have been deleted.",
  payload_too_large: "That is too large to send.",
  file_too_large: "That file is too large.",
  limit_reached: "You reached the limit for this agent.",
  revision_conflict: "This agent was changed somewhere else.",
};

export class Api {
  constructor(
    private readonly baseUrl: string,
    private readonly credentials: CredentialSource,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
    private readonly onUnauthorized: () => void = () => {},
  ) {}

  /** Headers for the widget preview, which talks to the API itself. */
  authHeaders(): Record<string, string> {
    if (this.credentials.cookieSession) return this.credentials.extraHeaders?.() ?? {};
    const a = this.credentials.authorization();
    return a ? { authorization: a } : {};
  }
  /** `include` when the person is signed in with a cookie session (also for the preview's own requests). */
  fetchCredentials(): RequestCredentials | undefined {
    return this.credentials.cookieSession ? "include" : undefined;
  }
  url(path: string) {
    return `${this.baseUrl}${path}`;
  }

  /** `quiet`: a 401 here is an answer (wrong password), not "your session ended". */
  private async request<T>(method: string, path: string, body?: unknown, quiet = false): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.url(path), {
        method,
        // a FormData body (picture upload) carries its own content type, with the boundary the browser picks
        headers: { ...this.authHeaders(), ...(body === undefined || body instanceof FormData ? {} : body instanceof Blob ? { "content-type": "application/octet-stream" } : { "content-type": "application/json" }) },
        body: body === undefined ? undefined : body instanceof FormData || body instanceof Blob ? body : JSON.stringify(body),
        credentials: this.fetchCredentials(),
      });
    } catch {
      throw new ApiError(0, "network", "We could not reach the server. Check your connection; your changes are kept here.");
    }
    const text = await res.text();
    let json: Record<string, unknown> = {};
    try {
      json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      /* non-JSON error page */
    }
    if (!res.ok) {
      const code = typeof json.error === "string" ? json.error : "server";
      if (res.status === 401 && !quiet) this.onUnauthorized();
      const message = (typeof json.message === "string" && json.message) || MESSAGES[code] || "Something went wrong on our side. Try again in a moment.";
      throw new ApiError(res.status, code, message, (json.issues as Issue[] | undefined) ?? [], json);
    }
    return json as T;
  }

  models = () => this.request<ModelRegistry>("GET", "/v1/models");
  listAgents = () => this.request<{ agents: AgentSummary[] }>("GET", "/v1/agents").then((r) => r.agents);
  /** Own agents and the ones other accounts shared with this person. */
  listAllAgents = () => this.request<{ agents: AgentSummary[]; shared?: SharedAgentSummary[] }>("GET", "/v1/agents").then((r) => ({ agents: r.agents, shared: r.shared ?? [] }));
  createAgent = (config: AgentDraft = {}) => this.request<AgentDetail>("POST", "/v1/agents", { config });
  deleteAgent = (id: string, deleteLeads = false) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}${deleteLeads ? "?deleteLeads=true" : ""}`);
  blockedSites = (id: string) => this.request<BlockedSites>("GET", `/v1/agents/${id}/blocked-sites`);
  allowSite = (id: string, what: { origin: string } | { suggestion: true }) => this.request<{ added: string[]; publishedVersion: number | null; revision: number }>("POST", `/v1/agents/${id}/blocked-sites/allow`, what);
  dismissSite = (id: string, what: { origin: string } | { suggestion: true }) => this.request<{ ok: true }>("POST", `/v1/agents/${id}/blocked-sites/dismiss`, what);
  checkSite = (id: string, url: string) => this.request<SiteReport>("POST", `/v1/agents/${id}/check-site`, { url });
  avatar = (id: string) => this.request<{ avatar: AvatarInfo | null }>("GET", `/v1/agents/${id}/avatar`).then((r) => r.avatar);
  uploadAvatar = (id: string, file: File, crop: AvatarCrop) => {
    const form = new FormData();
    form.append("file", file);
    form.append("zoom", String(crop.zoom));
    form.append("x", String(crop.x));
    form.append("y", String(crop.y));
    return this.request<{ avatar: AvatarInfo }>("POST", `/v1/agents/${id}/avatar`, form).then((r) => r.avatar);
  };
  recropAvatar = (id: string, crop: AvatarCrop) => this.request<{ avatar: AvatarInfo }>("PUT", `/v1/agents/${id}/avatar/crop`, crop).then((r) => r.avatar);
  removeAvatar = (id: string) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}/avatar`);
  /** The working copy for the crop screen, as an address the page can show (revoke it when done). */
  async avatarSourceUrl(id: string): Promise<string> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.url(`/v1/agents/${id}/avatar/source`), { headers: this.authHeaders(), credentials: this.fetchCredentials() });
    } catch {
      throw new ApiError(0, "network", "We could not reach the server. Check your connection; your changes are kept here.");
    }
    if (!res.ok) throw new ApiError(res.status, "no_source", "There is no picture to adjust.");
    return URL.createObjectURL(await res.blob());
  }
  getAgent = (id: string) => this.request<AgentDetail>("GET", `/v1/agents/${id}`);
  saveAgent = (id: string, config: AgentDraft, revision?: number) => this.request<AgentDetail>("PUT", `/v1/agents/${id}`, { config, revision });
  publish = (id: string) => this.request<AgentDetail>("POST", `/v1/agents/${id}/publish`, {});
  unpublish = (id: string) => this.request<AgentDetail>("POST", `/v1/agents/${id}/unpublish`, {});
  train = (id: string) => this.request<{ faqs: { status: string; error: string | null; chunks: number } }>("POST", `/v1/agents/${id}/train`, {});

  sources = (id: string) => this.request<{ sources: Source[] }>("GET", `/v1/agents/${id}/sources`).then((r) => r.sources);
  /** A document sent as raw bytes (no base64, nothing read into memory first): up to the file ceiling. */
  ocrEstimate = (id: string, sid: string) => this.request<OcrEstimate>("POST", `/v1/agents/${id}/sources/${sid}/ocr/estimate`, {});
  ocrStart = (id: string, sid: string, confirmUsd: number) => this.request<{ run: OcrRun }>("POST", `/v1/agents/${id}/sources/${sid}/ocr`, { confirmUsd });
  ocrStop = (id: string, sid: string) => this.request<{ stopping: boolean }>("POST", `/v1/agents/${id}/sources/${sid}/ocr/stop`, {});
  uploadSourceFile = (id: string, file: File) => this.request<Source>("PUT", `/v1/agents/${id}/sources/file?name=${encodeURIComponent(file.name)}`, file);
  addSource = (id: string, body: Record<string, unknown>) => this.request<Source>("POST", `/v1/agents/${id}/sources`, body);
  removeSource = (id: string, sid: string) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}/sources/${sid}`);
  driveFolders = () => this.request<{ enabled: boolean; shareWith: string | null; folders: DriveFolder[] }>("GET", "/v1/drive-folders", undefined, true);
  startDriveFolder = (link: string) => this.request<DriveFolder & { shareWith: string }>("POST", "/v1/drive-folders", { link });
  verifyDriveFolder = (id: string) => this.request<DriveFolder>("POST", `/v1/drive-folders/${encodeURIComponent(id)}/verify`, {});
  removeDriveFolder = (id: string) => this.request<{ removed: boolean }>("DELETE", `/v1/drive-folders/${encodeURIComponent(id)}`);
  integrations = () => this.request<{ providers: IntegrationState[] }>("GET", "/v1/integrations", undefined, true).then((r) => r.providers);
  connectIntegration = (provider: string, returnTo: string) => this.request<{ url: string }>("POST", `/v1/integrations/${provider}/connect`, { returnTo });
  disconnectIntegration = (provider: string, removeContent: boolean) => this.request<{ removed: boolean }>("POST", `/v1/integrations/${provider}/disconnect`, { removeContent });
  pickerToken = (provider: string) => this.request<PickerConfig>("POST", `/v1/integrations/${provider}/picker-token`, {});
  driveFiles = (id: string, sid: string) => this.request<DriveFiles>("GET", `/v1/agents/${id}/sources/${sid}/files`);
  reindexSource = (id: string, sid: string) => this.request<Source>("POST", `/v1/agents/${id}/sources/${sid}/reindex`, {});

  private leadQuery(f: LeadFilters, extra: Record<string, string> = {}) {
    const q = new URLSearchParams(extra);
    if (f.agentId) q.set("agentId", f.agentId);
    if (f.status) q.set("status", f.status);
    if (f.from) q.set("from", f.from);
    if (f.to) {
      // "to" is a day the person picked: include all of it (the API's end is exclusive)
      const d = new Date(`${f.to}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + 1);
      q.set("to", d.toISOString().slice(0, 10));
    }
    if (f.q?.trim()) q.set("q", f.q.trim());
    if (f.test) q.set("test", "true");
    return q.toString();
  }
  leads = (f: LeadFilters = {}, offset = 0, limit = 25) => this.request<{ leads: LeadSummary[]; total: number }>("GET", `/v1/leads?${this.leadQuery(f, { offset: String(offset), limit: String(limit) })}`);
  lead = (id: number) => this.request<LeadDetail>("GET", `/v1/leads/${id}`);
  deleteLead = (id: number) => this.request<{ ok: true }>("DELETE", `/v1/leads/${id}`);
  retryLead = (id: number, deliveryId?: number) => this.request<{ retried: number }>("POST", `/v1/leads/${id}/retry`, deliveryId ? { deliveryId } : {});
  eraseLeads = (email: string) => this.request<{ erased: number }>("POST", "/v1/leads/erase", { email });
  sendTestLead = (agentId: string) => this.request<{ id: number }>("POST", `/v1/agents/${agentId}/leads/test`, {});
  /** The CSV with the same filters, as text (the key is a header, so the browser cannot just follow a link). */
  async exportLeads(f: LeadFilters = {}): Promise<string> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.url(`/v1/leads/export.csv?${this.leadQuery(f)}`), { headers: this.authHeaders(), credentials: this.fetchCredentials() });
    } catch {
      throw new ApiError(0, "network", "We could not reach the server. Check your connection and try again.");
    }
    if (!res.ok) throw new ApiError(res.status, "server", "Could not prepare the file. Try again in a moment.");
    return res.text();
  }

  notifications = (hours = 24) => this.request<{ windowHours: number; notifications: AppNotification[] }>("GET", `/v1/notifications?hours=${hours}`);
  /** Owner only. `quiet`: a 404 means this deployment has no billing, which is an answer, not a failure. */
  billing = () => this.request<BillingInfo>("GET", "/v1/billing", undefined, true);
  checkout = (plan: string, interval: BillingInterval) => this.request<{ url: string; trialDays: number }>("POST", "/v1/billing/checkout", { plan, interval }, true);
  billingPortal = (flow?: "update") => this.request<{ url: string }>("POST", "/v1/billing/portal", flow ? { flow } : {}, true);
  keys = () => this.request<{ keys: ApiKeyInfo[] }>("GET", "/v1/api-keys").then((r) => r.keys);
  createKey = (name: string) => this.request<ApiKeyInfo & { key: string }>("POST", "/v1/api-keys", { name });
  revokeKey = (id: string) => this.request<ApiKeyInfo>("DELETE", `/v1/api-keys/${id}`);

  // ---- people, accounts and teams (docs/IDENTITY.md); `quiet` calls answer for themselves ----
  providers = () => this.request<{ google: boolean; password: boolean; signup?: SignupMode; signupDomains?: string[] }>("GET", "/v1/auth/providers", undefined, true);
  me = () => this.request<Me>("GET", "/v1/me", undefined, true);
  signup = (b: { email: string; password: string; name: string; language: string; accountName?: string; inviteToken?: string }) => this.request<{ ok: true; message: string } & Partial<Me>>("POST", "/v1/auth/signup", b, true);
  resendVerification = (email: string, language: string) => this.request<{ ok: true }>("POST", "/v1/auth/resend-verification", { email, language }, true);
  verifyEmail = (token: string) => this.request<Me>("POST", "/v1/auth/verify-email", { token }, true);
  login = (email: string, password: string, language: string) => this.request<Me>("POST", "/v1/auth/login", { email, password, language }, true);
  logout = () => this.request<unknown>("POST", "/v1/auth/logout", {}, true);
  logoutAll = () => this.request<{ ended: number }>("POST", "/v1/auth/logout-all", {}, true);
  forgotPassword = (email: string, language: string) => this.request<{ ok: true }>("POST", "/v1/auth/forgot-password", { email, language }, true);
  resetPassword = (token: string, password: string, language: string) => this.request<{ ok: true }>("POST", "/v1/auth/reset-password", { token, password, language }, true);
  updateMe = (b: { name?: string; language?: string }) => this.request<Me>("PATCH", "/v1/me", b, true);
  changePassword = (current: string, next: string) => this.request<{ ok: true }>("POST", "/v1/me/password", { current, next }, true);
  sessions = () => this.request<{ sessions: SessionInfo[] }>("GET", "/v1/me/sessions", undefined, true).then((r) => r.sessions);
  endSession = (id: string) => this.request<{ ok: true }>("DELETE", `/v1/me/sessions/${id}`, undefined, true);
  switchAccount = (accountId: string) => this.request<Me>("POST", "/v1/me/active-account", { accountId }, true);
  createAccount = (name: string) => this.request<{ id: string; name: string }>("POST", "/v1/accounts", { name }, true);
  team = () => this.request<TeamInfo>("GET", "/v1/team", undefined, true);
  invite = (email: string, role: string) => this.request<{ id: string; email: string }>("POST", "/v1/team/invitations", { email, role }, true);
  revokeInvite = (id: string) => this.request<{ ok: true }>("DELETE", `/v1/team/invitations/${id}`, undefined, true);
  setRole = (userId: string, role: string) => this.request<{ ok: true }>("PATCH", `/v1/team/members/${userId}`, { role }, true);
  removeMember = (userId: string) => this.request<{ ok: true }>("DELETE", `/v1/team/members/${userId}`, undefined, true);
  // ---- sharing one agent, and handing it over (docs/SHARING.md) ----
  sharing = (id: string) => this.request<SharingInfo>("GET", `/v1/agents/${id}/sharing`);
  shareAgent = (id: string, email: string, role: ShareRole) => this.request<{ id: string; email: string }>("POST", `/v1/agents/${id}/shares`, { email, role });
  setShareRole = (id: string, userId: string, role: ShareRole) => this.request<{ ok: true }>("PATCH", `/v1/agents/${id}/shares/${userId}`, { role });
  removeShare = (id: string, userId: string) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}/shares/${userId}`);
  revokeShareInvite = (id: string, invId: string) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}/share-invitations/${invId}`);
  lookupShare = (token: string) => this.request<{ agentName: string; accountName: string; email: string; role: ShareRole; hasLogin: boolean }>("POST", "/v1/share-invitations/lookup", { token }, true);
  acceptShare = (b: { token: string; name?: string; password?: string; language?: string }) => this.request<Partial<Me> & { sharedAgentId: string }>("POST", "/v1/share-invitations/accept", b, true);
  offerTransfer = (id: string, b: { email: string; confirmName: string; keepAccess: "none" | ShareRole }) => this.request<{ id: string; toEmail: string; expiresAt: string }>("POST", `/v1/agents/${id}/transfer`, b);
  cancelTransfer = (id: string) => this.request<{ ok: true }>("DELETE", `/v1/agents/${id}/transfer`);
  transfers = () => this.request<{ incoming: TransferBrief[]; outgoing: TransferBrief[] }>("GET", "/v1/transfers", undefined, true); // quiet: an API key has no offers (401), which is an answer, not "your session ended"
  lookupTransfer = (ref: { token?: string; id?: string }) => this.request<TransferOffer>("POST", "/v1/transfers/lookup", ref, true);
  acceptTransfer = (ref: { token?: string; id?: string }, accountId: string) => this.request<{ ok: true; agentId: string; accountId: string; leadDestinationsReset: boolean; overPlan?: { message: string; upgradeUrl?: string; reason?: string }; people: { userId: string; name: string; email: string; role: ShareRole }[] }>("POST", "/v1/transfers/accept", { ...ref, accountId });
  declineTransfer = (ref: { token?: string; id?: string }) => this.request<{ ok: true }>("POST", "/v1/transfers/decline", ref);

  lookupInvite = (token: string) => this.request<{ accountName: string; email: string; role: Role; hasLogin: boolean }>("POST", "/v1/invitations/lookup", { token }, true);
  acceptInvite = (b: { token: string; name?: string; password?: string; language?: string }) => this.request<Me & { joined: string }>("POST", "/v1/invitations/accept", b, true);
  account = () => this.request<AccountInfo>("GET", "/v1/account", undefined, true);
  updateAccount = (b: { name?: string; language?: string }) => this.request<{ id: string; name: string; language: string }>("PATCH", "/v1/account", b, true);
  deleteAccount = (confirmName: string) => this.request<{ ok: true; purgeAfter: string }>("DELETE", "/v1/account", { confirmName }, true);
  mfaSetup = () => this.request<{ secret: string; otpauthUrl: string }>("POST", "/v1/me/mfa/setup", {}, true);
  mfaVerify = (code: string) => this.request<{ ok: true; recoveryCodes?: string[] }>("POST", "/v1/me/mfa/verify", { code }, true);
  mfaRecovery = (code: string) => this.request<{ ok: true; left: number }>("POST", "/v1/me/mfa/recovery", { code }, true);
  mfaNewRecoveryCodes = () => this.request<{ recoveryCodes: string[] }>("POST", "/v1/me/mfa/recovery-codes", {}, true);
  mfaDismissNudge = () => this.request<{ ok: true }>("POST", "/v1/me/mfa/dismiss", {}, true);
  mfaDisable = (code: string) => this.request<{ ok: true }>("DELETE", "/v1/me/mfa", { code }, true);
  exportStatus = (token: string) => this.request<{ accountName: string; purgeAfter: string; requested: boolean }>("POST", "/v1/exports/status", { token }, true);
  exportRequest = (token: string) => this.request<{ ok: true; expiresAt: string }>("POST", "/v1/exports/request", { token }, true);
  /** A file of the export, as text (the token is sent in the body, never in a web address). */
  async exportDownload(token: string, file: "json" | "csv"): Promise<string> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.url("/v1/exports/download"), { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, file }) });
    } catch {
      throw new ApiError(0, "network", "We could not reach the server. Check your connection and try again.");
    }
    if (!res.ok) throw new ApiError(res.status, "invalid_token", "This download link has expired or is not valid.");
    return res.text();
  }
  googleStartUrl = (lang: string, invite?: string) => this.url(`/v1/auth/google/start?app=settings&lang=${lang}${invite ? `&invite=${encodeURIComponent(invite)}` : ""}`);
  signupInvitation = (token: string, language: string) => this.request<{ email: string }>("POST", "/v1/auth/signup-invitation/lookup", { token, language }, true);
  activity = (agentId: string, f: ActivityFilters = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "") q.set(k, String(v));
    return this.request<{ total: number; activity: ActivityItem[] }>("GET", `/v1/agents/${agentId}/activity?${q}`);
  };
  versions = (agentId: string) => this.request<{ versions: VersionItem[] }>("GET", `/v1/agents/${agentId}/versions`).then((r) => r.versions);
  revertTo = (agentId: string, version: number) => this.request<{ revertedFrom: number }>("POST", `/v1/agents/${agentId}/versions/${version}/revert`, {});

  previewConfigUrl = (id: string) => this.url(`/v1/agents/${id}/preview/widget-config`);
  previewChatUrl = (id: string) => this.url(`/v1/agents/${id}/preview/chat`);
  modelMigration = (id: string) => this.request<ModelMigration>("GET", `/v1/agents/${id}/model-migration`, undefined, true);
  acknowledgeModelMigration = (id: string) => this.request<ModelMigration>("POST", `/v1/agents/${id}/model-migration/acknowledge`, {});
  switchModelMigration = (id: string) => this.request<ModelMigration>("POST", `/v1/agents/${id}/model-migration/switch`, {});
  /** One question to the agent answered from the new search index. Read-only for the knowledge. */
  testWithNewModel = (id: string, message: string) => this.request<{ text: string }>("POST", `/v1/agents/${id}/preview/chat?embedding=new`, { message, stream: false });
}
